import React, { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getSamples } from '../../services/sampleService';
import { greenBeanService } from '../../services/greenBeanService';
import { profileService } from '../../services/profileService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Search } from 'lucide-react';
import { format } from 'date-fns';
import { ProfileManager } from '../../components/inventory/ProfileManager';
import { useQuery } from '@tanstack/react-query';
import { QCStatusButton } from '../../components/inventory/QCStatusButton';
import { Modal } from '../../components/common/Modal';
import { GreenBeanProfileForm } from '../purchasing/GreenBeanProfileForm';
import { CuppingProfileForm } from '../inventory/CuppingProfileForm';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../utils/date';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { Pagination } from '../../components/common/Pagination';
import { usePaginationState } from '../../hooks/usePaginationState';

import type { CuppingProfile } from '../../services/profileService';
import type { GreenBeanProfile } from '../../types/greenBean';

export const SampleList: React.FC = () => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('samples', 'write');
  const readOnly = !canWrite;
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [dateFilter, setDateFilter] = useState('');
  const [sortBy, setSortBy] = useState('sampleNumber');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { page, size: limit, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 50 });

  // QC Modal State
  const [qcModal, setQcModal] = useState<{
    isOpen: boolean;
    type: 'bean' | 'cupping';
    entityId: string;
    entityType: 'LotType' | 'SampleType';
    lotId?: string;
  }>({
    isOpen: false,
    type: 'bean',
    entityId: '',
    entityType: 'LotType',
  });

  const { data: greenBeanProfilesData, refetch: refetchGreenBeans } = useQuery({
    queryKey: ['green-beans'],
    queryFn: greenBeanService.getAll,
  });

  const { data: cuppingProfilesData, refetch: refetchCupping } = useQuery({
    queryKey: ['cupping-profiles'],
    queryFn: profileService.getAllCuppingProfiles,
  });

  const greenBeanProfiles = greenBeanProfilesData || [];
  const cuppingProfiles = cuppingProfilesData || [];

  const { 
    data: samplesData, 
    isLoading: loading, 
    refetch: fetchSamples 
  } = useQuery({
    queryKey: ['samples'],
    queryFn: () => getSamples({ limit: 1000 }),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });

  const allSamples = useMemo(() => samplesData?.data || [], [samplesData?.data]);

  const filteredSamples = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return allSamples.filter(sample => {
      const matchesSearch = 
        query.length === 0 || 
        sample.sampleNumber.toLowerCase().includes(query) || 
        (sample.itemName || '').toLowerCase().includes(query) ||
        (sample.itemCode || '').toLowerCase().includes(query);
      
      const matchesDate = !dateFilter || (sample.receivedDate && sample.receivedDate.startsWith(dateFilter));
      
      return matchesSearch && matchesDate;
    });
  }, [allSamples, searchTerm, dateFilter]);

  const sortedSamples = useMemo(() => {
    return [...filteredSamples].sort((a, b) => {
      const aVal = a[sortBy as keyof typeof a];
      const bVal = b[sortBy as keyof typeof b];

      if (aVal === bVal) return 0;
      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;

      const result = aVal < bVal ? -1 : 1;
      return sortOrder === 'asc' ? result : -result;
    });
  }, [filteredSamples, sortBy, sortOrder]);

  const samples = useMemo(() => {
    const start = (page - 1) * limit;
    return sortedSamples.slice(start, start + limit);
  }, [sortedSamples, page, limit]);

  const total = sortedSamples.length;

  resetOnChange([searchTerm, dateFilter, sortBy, sortOrder]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const getGreenBeanQC = (entityId: string) => {
    const profile = greenBeanProfiles.find(p => p.sample_id === entityId);
    return profile?.sample_physical_check;
  };


  const handleQCClick = (type: 'bean' | 'cupping', entityId: string, entityType: 'LotType' | 'SampleType', lotId?: string) => {
    setQcModal({
      isOpen: true,
      type,
      entityId,
      entityType,
      lotId,
    });
  };

  const getInitialQCData = () => {
    const { type, entityId, entityType } = qcModal;
    if (type === 'bean') {
      return entityType === 'LotType' 
        ? greenBeanProfiles.find(p => p.lot_id === entityId)
        : greenBeanProfiles.find(p => p.sample_id === entityId);
    } else {
      return entityType === 'LotType'
        ? cuppingProfiles.find(p => p.lot_id === entityId)
        : cuppingProfiles.find(p => p.sample_id === entityId);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">{t('purchasing.sampleList.title')}</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-end gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="relative flex-1 w-full">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">{t('purchasing.sampleList.filters.search')}</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              id="search"
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder={t('purchasing.sampleList.filters.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value) {
                  setSearchParams({ search: e.target.value });
                } else {
                  setSearchParams({});
                }
              }}
            />
          </div>
        </div>
        <div className="w-full sm:w-48">
          <label htmlFor="receivedDate" className="block text-sm font-medium text-gray-700 mb-1">{t('purchasing.sampleList.filters.date')}</label>
          <input
            id="receivedDate"
            type="date"
            className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            onClick={openNativeDatePickerSafely}
            onKeyDown={preventManualDateEntry}
            onPaste={preventManualDatePasteOrDrop}
            onDrop={preventManualDatePasteOrDrop}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <LoadingSpinner text={t('purchasing.sampleList.loading')} />
        ) : samples.length === 0 ? (
          <div className="p-8 text-center text-gray-500 italic">{t('purchasing.sampleList.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('sampleNumber')}
                  >
                    {t('purchasing.sampleList.table.sampleNumber')} {renderSortIcon('sampleNumber')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('purchasing.sampleList.table.item')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('purchasing.sampleList.table.notes')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('purchasing.sampleList.table.source')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('purchasing.sampleList.table.greenBeanProfile')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('purchasing.sampleList.table.roastProfile')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('purchasing.sampleList.table.tasteProfile')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('receivedDate')}
                  >
                    {t('purchasing.sampleList.table.receivedDate')} {renderSortIcon('receivedDate')}
                  </th>
                  <th 
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('qaPassed')}
                  >
                    {t('purchasing.sampleList.table.qualityControl')} {renderSortIcon('qaPassed')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {samples.map((sample) => (
                  <tr key={sample.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {sample.sampleNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="font-medium text-gray-900">{sample.itemName || t('purchasing.sampleList.values.unknownItem')}</div>
                      <div className="text-gray-500 text-xs">{sample.itemCode || sample.inventoryItemId}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={sample.notes}>
                      {sample.notes || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {sample.lotId && sample.lotNumber ? (
                        <Link to={`/inventory/lots/${sample.lotId}`} className="text-indigo-600 hover:underline">
                          {sample.lotNumber}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 text-center">
                      {sample.lotId ? (
                        <span className="text-gray-400 text-xs italic">{t('purchasing.sampleList.values.fillProfileInLot')}</span>
                      ) : (
                        <ProfileManager
                          entityType="SampleType"
                          entityId={sample.id}
                          profileType="greenBean"
                          existingProfile={sample.greenBeanProfileId ? { id: sample.greenBeanProfileId } : undefined}
                          onProfileUpdate={() => fetchSamples()}
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 text-center">
                      {sample.lotId ? (
                        <span className="text-gray-400 text-xs italic">{t('purchasing.sampleList.values.fillProfileInLot')}</span>
                      ) : (
                        <ProfileManager
                          entityType="SampleType"
                          entityId={sample.id}
                          profileType="roast"
                          itemName={sample.itemName || t('purchasing.sampleList.values.unknownItem')}
                          existingProfile={sample.roastProfileId ? { id: sample.roastProfileId } : undefined}
                          onProfileUpdate={() => fetchSamples()}
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 text-center">
                      {sample.lotId ? (
                        <span className="text-gray-400 text-xs italic">{t('purchasing.sampleList.values.fillProfileInLot')}</span>
                      ) : (
                        <ProfileManager
                          entityType="SampleType"
                          entityId={sample.id}
                          profileType="cupping"
                          existingProfile={sample.cuppingProfileId ? { id: sample.cuppingProfileId } : undefined}
                          onProfileUpdate={() => fetchSamples()}
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {sample.receivedDate ? format(new Date(sample.receivedDate), 'dd MMM yyyy') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center space-x-2">
                      {sample.lotId ? (
                        <span className="text-gray-400 text-xs italic">{t('purchasing.sampleList.values.fillProfileInLot')}</span>
                      ) : (
                        <>
                          <QCStatusButton
                            type="Bean"
                            status={getGreenBeanQC(sample.id)}
                            onClick={() => handleQCClick('bean', sample.id, 'SampleType', sample.lotId)}
                          />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {!loading && (
          <Pagination
            page={page}
            pageSize={limit}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setSize}
          />
        )}
      </div>

      {/* QC Modal */}
      {qcModal.isOpen && (
        <Modal
          isOpen={qcModal.isOpen}
          onClose={() => setQcModal(prev => ({ ...prev, isOpen: false }))}
          title={
            qcModal.type === 'bean'
              ? t('purchasing.sampleList.modal.greenBeanQc')
              : t('purchasing.sampleList.modal.cuppingQc')
          }
          maxWidth={qcModal.type === 'bean' ? '4xl' : '6xl'}
        >
          {qcModal.type === 'bean' ? (
            <GreenBeanProfileForm
              entityType={qcModal.entityType}
              entityId={qcModal.entityId}
              initialData={getInitialQCData() as Partial<GreenBeanProfile>}
              readOnly={readOnly}
              onSuccess={() => {
                setQcModal(prev => ({ ...prev, isOpen: false }));
                fetchSamples();
                refetchGreenBeans();
              }}
              onCancel={() => setQcModal(prev => ({ ...prev, isOpen: false }))}
            />
          ) : (
            <CuppingProfileForm
              entityType={qcModal.entityType}
              entityId={qcModal.entityId}
              lotId={qcModal.lotId}
              initialData={getInitialQCData() as Partial<CuppingProfile>}
              readOnly={readOnly}
              onSuccess={() => {
                setQcModal(prev => ({ ...prev, isOpen: false }));
                fetchSamples();
                refetchCupping();
              }}
              onCancel={() => setQcModal(prev => ({ ...prev, isOpen: false }))}
            />
          )}
        </Modal>
      )}
    </div>
  );
};
